import { Controller } from '@hotwired/stimulus';

// The "MET WHEN SIGNED" call-out on a goal's row, kept in step with the entry band while the
// therapist works on the report: pick a status or type a target and it appears, or changes;
// clear both and it goes. The server renders the same words on load (progress_reports/goals/_goal),
// this only follows the fields from there.
//
// Mounted on the goal card. The status is the Impulse select's hidden carrier, which is set before
// the select announces `awc-autocomplete:commit` / `awc-autocomplete:clear`. The target is a number
// input (`input` covers it), or, for a goal graded in its own words, an Impulse select like the
// status, whose carrier holds the chosen grade's words as data-text, or, for a Range of motion
// goal, two boxes, a start and an end ("target 0-150°"). The events bubble to the card,
// which is where the actions sit. The words match ProgressReports::Proposals::Proposal#status_label
// and _goal's pending_words (ProgressReportsHelper#goals_report_proposed_target).
//
// Targets:
//   callout  the wrapper on the row (hidden while nothing is proposed)
//   words    the text inside the badge, before " when signed"
const LABELS = { met: 'Met', discontinue: 'Discontinued' };

export default class extends Controller {
  static targets = ['callout', 'words'];

  refresh() {
    if (!this.hasCalloutTarget) return;
    const words = [];
    const status = this.statusValue();
    const target = this.targetWords();
    if (status) words.push(LABELS[status] || status);
    if (target) words.push(target);
    this.calloutTarget.hidden = words.length === 0;
    if (this.hasWordsTarget) this.wordsTarget.textContent = words.join(', ');
  }

  statusValue() {
    const field = this.element.querySelector('input[data-behavior="hidden-field"][name$="[proposed_status]"]');
    return field ? field.value : '';
  }

  // "target Good" for a grade picked from the select, "target 90%" or "target 30 min" for a typed
  // number (the input carries its unit in data-unit, as the server words it once saved), "target
  // 0-150°" for a range, "target 8/10" for a counting goal's pair ("?" for a box still empty).
  targetWords() {
    const start = this.element.querySelector('input[name$="[proposed_target][low]"]');
    if (start) {
      const end = this.element.querySelector('input[name$="[proposed_target][high]"]');
      const [from, to] = [start, end].map((box) => (box ? box.value.trim() : ''));
      if (!from && !to) return '';
      return `target ${from || '?'}-${to || '?'}${start.dataset.unit || ''}`;
    }
    const correct = this.element.querySelector('input[name$="[proposed_target][correct]"]');
    if (correct) {
      const total = this.element.querySelector('input[name$="[proposed_target][total]"]');
      const [got, outOf] = [correct, total].map((box) => (box ? box.value.trim() : ''));
      if (!got && !outOf) return '';
      return `target ${got || '?'}/${outOf || '?'}`;
    }
    const field = this.element.querySelector('input[name$="[proposed_target]"]');
    const value = field ? field.value.trim() : '';
    if (!value) return '';
    if (field.dataset.behavior === 'hidden-field') return `target ${field.dataset.text || value}`;
    return `target ${value}${field.dataset.unit === undefined ? '%' : field.dataset.unit}`;
  }
}
